import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { RotateCw, FlipHorizontal, FlipVertical, Crop, X, Check, Sun, Contrast, Droplets } from 'lucide-react';

const FILTERS = [
  { name: 'None', value: '', css: '' },
  { name: 'Warm', value: 'warm', css: 'sepia(30%) saturate(120%)' },
  { name: 'Cool', value: 'cool', css: 'hue-rotate(20deg) saturate(90%)' },
  { name: 'B&W', value: 'grayscale', css: 'grayscale(100%)' },
  { name: 'Vintage', value: 'vintage', css: 'sepia(50%) contrast(90%) brightness(95%)' },
  { name: 'Vivid', value: 'vivid', css: 'saturate(160%) contrast(110%)' },
  { name: 'Fade', value: 'fade', css: 'brightness(110%) saturate(70%) contrast(90%)' },
  { name: 'Drama', value: 'drama', css: 'contrast(130%) brightness(90%) saturate(110%)' },
];

const ImageEditor = ({ imageUrl, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isCropping, setIsCropping] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const imgContainerRef = useRef(null);

  const getFilterCSS = useCallback(() => {
    const base = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    const filter = FILTERS.find(f => f.value === selectedFilter);
    return filter?.css ? `${base} ${filter.css}` : base;
  }, [brightness, contrast, saturation, selectedFilter]);

  const getTransform = useCallback(() => {
    const parts = [];
    parts.push(`rotate(${rotation}deg)`);
    if (flipH) parts.push('scaleX(-1)');
    if (flipV) parts.push('scaleY(-1)');
    return parts.join(' ');
  }, [rotation, flipH, flipV]);

  const handleCropMouseDown = (e) => {
    if (!isCropping) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCropStart({ x, y });
    setCropEnd({ x, y });
    setIsDragging(true);
  };

  const handleCropMouseMove = (e) => {
    if (!isDragging || !isCropping) return;
    const rect = imgContainerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setCropEnd({ x, y });
  };

  const handleCropMouseUp = () => setIsDragging(false);

  const applyCrop = () => {
    if (!cropStart || !cropEnd) return;
    setIsCropping(false);
    // Crop is visual only - applied on save
  };

  const handleSave = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let sw = img.width, sh = img.height;
      if (rotation === 90 || rotation === 270) {
        canvas.width = sh;
        canvas.height = sw;
      } else {
        canvas.width = sw;
        canvas.height = sh;
      }

      ctx.filter = getFilterCSS();
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      if (flipH) ctx.scale(-1, 1);
      if (flipV) ctx.scale(1, -1);

      const dw = rotation === 90 || rotation === 270 ? sh : sw;
      const dh = rotation === 90 || rotation === 270 ? sw : sh;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();

      // Apply crop if set
      if (cropStart && cropEnd) {
        const x1 = Math.min(cropStart.x, cropEnd.x) / 100 * canvas.width;
        const y1 = Math.min(cropStart.y, cropEnd.y) / 100 * canvas.height;
        const cw = Math.abs(cropEnd.x - cropStart.x) / 100 * canvas.width;
        const ch = Math.abs(cropEnd.y - cropStart.y) / 100 * canvas.height;
        if (cw > 10 && ch > 10) {
          const cropCanvas = document.createElement('canvas');
          cropCanvas.width = cw;
          cropCanvas.height = ch;
          cropCanvas.getContext('2d').drawImage(canvas, x1, y1, cw, ch, 0, 0, cw, ch);
          onSave(cropCanvas.toDataURL('image/jpeg', 0.9));
          return;
        }
      }

      onSave(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.src = imageUrl;
  };

  const resetAll = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setSelectedFilter('');
    setCropStart(null);
    setCropEnd(null);
    setIsCropping(false);
  };

  const cropRect = cropStart && cropEnd ? {
    left: `${Math.min(cropStart.x, cropEnd.x)}%`,
    top: `${Math.min(cropStart.y, cropEnd.y)}%`,
    width: `${Math.abs(cropEnd.x - cropStart.x)}%`,
    height: `${Math.abs(cropEnd.y - cropStart.y)}%`,
  } : null;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border dark:border-gray-700 overflow-hidden" data-testid="image-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotate" data-testid="rotate-btn">
            <RotateCw className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setFlipH(!flipH)} className={flipH ? 'text-primary' : ''} title="Flip H">
            <FlipHorizontal className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setFlipV(!flipV)} className={flipV ? 'text-primary' : ''} title="Flip V">
            <FlipVertical className="w-4 h-4" />
          </Button>
          <Button variant={isCropping ? 'default' : 'ghost'} size="sm" onClick={() => { setIsCropping(!isCropping); setCropStart(null); setCropEnd(null); }} title="Crop" data-testid="crop-btn">
            <Crop className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={resetAll} className="text-xs text-gray-500">Reset</Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="cancel-edit-btn">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={handleSave} className="bg-primary text-white" data-testid="save-edit-btn">
            <Check className="w-4 h-4 mr-1" /> Apply
          </Button>
        </div>
      </div>

      {/* Image Preview */}
      <div
        ref={imgContainerRef}
        className="relative flex items-center justify-center bg-black/5 dark:bg-black/20 overflow-hidden"
        style={{ maxHeight: '300px' }}
        onMouseDown={handleCropMouseDown}
        onMouseMove={handleCropMouseMove}
        onMouseUp={handleCropMouseUp}
        onMouseLeave={handleCropMouseUp}
      >
        <img
          src={imageUrl}
          alt="Edit"
          className="max-h-[300px] w-auto object-contain"
          style={{ filter: getFilterCSS(), transform: getTransform() }}
          draggable={false}
        />
        {isCropping && cropRect && (
          <div className="absolute border-2 border-white border-dashed bg-white/10 pointer-events-none" style={cropRect} />
        )}
        {isCropping && (
          <div className="absolute inset-0 cursor-crosshair" />
        )}
      </div>

      {/* Adjustments */}
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Sun className="w-3 h-3 text-yellow-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Brightness</span>
              <span className="text-xs text-gray-400 ml-auto">{brightness}%</span>
            </div>
            <Slider value={[brightness]} onValueChange={([v]) => setBrightness(v)} min={30} max={200} step={5} className="w-full" />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Contrast className="w-3 h-3 text-gray-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Contrast</span>
              <span className="text-xs text-gray-400 ml-auto">{contrast}%</span>
            </div>
            <Slider value={[contrast]} onValueChange={([v]) => setContrast(v)} min={30} max={200} step={5} className="w-full" />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              <Droplets className="w-3 h-3 text-blue-500" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Saturation</span>
              <span className="text-xs text-gray-400 ml-auto">{saturation}%</span>
            </div>
            <Slider value={[saturation]} onValueChange={([v]) => setSaturation(v)} min={0} max={200} step={5} className="w-full" />
          </div>
        </div>

        {/* Filters */}
        <div>
          <span className="text-xs text-gray-500 dark:text-gray-400 block mb-2">Filters</span>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setSelectedFilter(f.value)}
                className={`flex-shrink-0 rounded-lg border-2 transition-all ${selectedFilter === f.value ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'}`}
                data-testid={`filter-${f.value || 'none'}`}
              >
                <div className="w-14 h-14 rounded-md overflow-hidden bg-gray-200">
                  <img src={imageUrl} alt={f.name} className="w-full h-full object-cover" style={{ filter: f.css || 'none' }} />
                </div>
                <span className="text-[10px] text-gray-600 dark:text-gray-400 block text-center mt-0.5">{f.name}</span>
              </button>
            ))}
          </div>
        </div>

        {isCropping && (
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg">
            <Crop className="w-3 h-3" />
            <span>Click and drag on the image to select crop area</span>
            {cropStart && cropEnd && (
              <Button size="sm" variant="outline" onClick={applyCrop} className="ml-auto text-xs h-6">Apply Crop</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageEditor;
